import type {GridApiCommon} from '@mui/x-data-grid';
import type {GridDensity, GridRowClassNameParams} from '@mui/x-data-grid-premium';
import type {RefObject} from 'react';
import {gridRowNodeSelector} from '@mui/x-data-grid-premium';

/** MUI compact density factor — used when deriving rowHeight from a target min height. */
export const COMPACT_DENSITY_FACTOR = 0.7;

/** Data grids always use compact density; row size is controlled separately. */
export const DATA_GRID_DENSITY: GridDensity = 'compact';

export const DATA_GRID_ROW_MIN_HEIGHT = {
    small: 36,
    medium: 72,
} as const;

/** Rendered height of the header filter row (input filters), after compact density. */
export const DATA_GRID_HEADER_FILTER_HEIGHT = 52;

export type DataGridRowSize = keyof typeof DATA_GRID_ROW_MIN_HEIGHT;

export type DataGridRowMinHeight = (typeof DATA_GRID_ROW_MIN_HEIGHT)[DataGridRowSize];

export const getDataGridRowMinHeight = (size: DataGridRowSize): DataGridRowMinHeight =>
    DATA_GRID_ROW_MIN_HEIGHT[size];

export const getDataGridMinRowClassName = (minHeight: DataGridRowMinHeight): string =>
    `data-grid-min-row-${minHeight}`;

/**
 * Prop value so compact density (×0.7) produces the target rendered height.
 * Use with getRowHeight={() => 'auto'} + min-row class when content can wrap instead.
 */
export const getDataGridHeightForCompactMin = (targetHeight: number): number =>
    Math.ceil(targetHeight / COMPACT_DENSITY_FACTOR);

export const buildDataGridClassName = (
    options: {
        minHeight: DataGridRowMinHeight;
        autoRowHeight?: boolean;
        extra?: string;
    },
): string => {
    const parts = [
        options.autoRowHeight ? 'base-table-muix auto-row-height-centered' : undefined,
        getDataGridMinRowClassName(options.minHeight),
        options.extra,
    ].filter(Boolean);

    return parts.join(' ');
};

export const DATA_GRID_GROUP_ROW_EXPANDED_CLASS = 'data-grid-group-row-expanded';
export const DATA_GRID_GROUP_CHILD_ROW_CLASS = 'data-grid-group-child-row';

/** Row grouping: expanded group rows and their visible child rows. */
export const getDataGridGroupingRowClassName = (
    apiRef: RefObject<GridApiCommon | null>,
    params: GridRowClassNameParams,
): string => {
    const rowNode = gridRowNodeSelector(apiRef, params.id);
    if (!rowNode) {
        return '';
    }

    if (rowNode.type === 'group' && rowNode.childrenExpanded) {
        return DATA_GRID_GROUP_ROW_EXPANDED_CLASS;
    }

    if (rowNode.type === 'leaf' && rowNode.depth > 0) {
        return DATA_GRID_GROUP_CHILD_ROW_CLASS;
    }

    return '';
};
